import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useMemo,
  useEffect,
} from "react";

import { Grid } from "@mui/material";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input, AutoSuggest } from "../../../../../../../../components/Form";
import { Color } from "../../../../../../../../components/Form/Button";
import {
  FieldViewWithGridProps,
  SectionView,
} from "../../../../../../../../components/Generic/SideView/SectionView";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import { useAppState } from "../../../../../../../../context/StateContext";
import useYupValidation, {
  useHandleValidate,
  useYupSchemas,
} from "../../../../../../../../hooks/useYupValidation";
import { CommodityCode, RegionCommodityCode } from "../../../../../../../../models/commodity.code";
import { Error } from "../../../../../../../../models/error";
import CommodityCodeService from "../../../../../../../../services/commodity.code.service";
import { Status } from "../../../../../../../../util/enums";
import useActions from "../../hooks/useActions";
import {
  CodeLevelType,
  CodeType,
  CodeTypeNumber,
  formatCommodityCode,
  formatHSCode,
  REGEX_TEST_8_TO_10_DIGITS,
  REGEX_TEST_NUMERIC,
} from "../../utils";
import { Modal, Subtitle, NumberInput } from "./styles";

export enum ManageModalMode {
  CREATE = "CREATE",
  UPDATE = "UPDATE",
  CLONE = "CLONE",
}

export interface ManageCommodityCodeHandles {
  open: (mode: ManageModalMode, commodityCode?: CommodityCode) => void;
  close: () => void;
}

interface Props {}

const ManageCommodityCodeModal: ForwardRefRenderFunction<ManageCommodityCodeHandles, Props> = (
  _,
  ref
) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [mode, setMode] = useState(ManageModalMode.CREATE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [responseError, setResponseError] = useState<Error>();
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const defaultCommodityCode = { status: Status.ACTIVE } as CommodityCode;

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [commodityCode, setCommodityCode] = useState<CommodityCode | undefined>(
    defaultCommodityCode as CommodityCode
  );
  const [selectedParent, setSelectedParent] = useState<CommodityCode | undefined>();

  const { useCreateCommodityCode, useUpdateCommodityCode, variables } = useActions();

  const { mutate: createCommodityCode, isLoading: creatingCommodityCode } =
    useCreateCommodityCode();
  const { mutate: updateCommodityCode, isLoading: updatingCommodityCode } =
    useUpdateCommodityCode();

  const isLoading = creatingCommodityCode || updatingCommodityCode;

  const clearFields = () => {
    setCommodityCode(defaultCommodityCode);
    setSelectedParent(undefined);
    setMode(ManageModalMode.CREATE);
    setHasUnsavedChanges(false);
  };

  const clearErrors = () => {
    setErrors([]);
    setResponseError(undefined);
    setHasFormBeenSubmitted(false);
  };

  const openModal = useCallback((mode: ManageModalMode, code?: CommodityCode) => {
    setModalOpened(true);
    setMode(mode);

    if (mode === ManageModalMode.CLONE && code) {
      if (CodeLevelType[code.level] === CodeType.HS_CODE) {
        setCommodityCode({
          parent: code.hscode,
          statisticalClassification: "",
          status: code.status,
        } as CommodityCode);
        setSelectedParent({
          sectionId: code.sectionId,
          chapterId: code.chapterId,
          sectionEntity: code.sectionEntity,
          chapterEntity: code.chapterEntity,
          hscode: code.hscode,
          description: code.description,
        } as CommodityCode);
      }

      if (CodeLevelType[code.level] === CodeType.COMMODITY_CODE) {
        setCommodityCode({
          hscode: code.hscode,
          parent: code.parent,
          statisticalClassification: code.statisticalClassification,
          status: code.status,
          regions: code.regions,
        } as CommodityCode);

        setSelectedParent({
          sectionId: code.sectionId,
          chapterId: code.chapterId,
          sectionEntity: code.sectionEntity,
          chapterEntity: code.chapterEntity,
          hscode: code.parent,
          description: code.description,
        } as CommodityCode);
      }
    }

    if (mode === ManageModalMode.UPDATE && code) {
      setCommodityCode(code);
      setSelectedParent({
        sectionEntity: code.sectionEntity,
        chapterEntity: code.chapterEntity,
        hscode: code.parent,
        description: code.description,
      } as CommodityCode);
    }
  }, []);

  const closeModal = useCallback(() => {
    clearFields();
    clearErrors();
    setModalOpened(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const handleUpdateState = useCallback((attribute: keyof CommodityCode, value: any) => {
    setCommodityCode((state: any) => ({ ...state, [attribute]: value }));
    setHasUnsavedChanges(true);
  }, []);

  const { stringRequired, arrayRequired } = useYupSchemas(translate);

  const schema = yup.object().shape({
    parent: stringRequired,
    hscode: stringRequired.matches(REGEX_TEST_8_TO_10_DIGITS, translate("typeInTheLast2To4Digits")),
    statisticalClassification: stringRequired,
    regions: arrayRequired,
  });

  const isValid = () => {
    const validated = useHandleValidate(commodityCode, schema, setErrors);

    return validated;
  };

  const handleCreateCommodityCode = (commodityCode: CommodityCode) => {
    createCommodityCode(commodityCode, {
      onSuccess: () => {
        closeModal();
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && errorAxios?.response?.data.field !== "unknown")
          setResponseError(errorAxios?.response?.data);
      },
    });
  };

  const handleUpdateCommodityCode = (commodityCode: CommodityCode) => {
    if (!commodityCode.id) return;

    updateCommodityCode(
      { id: commodityCode.id, commodityCode },
      {
        onSuccess: () => {
          closeModal();
        },
        onError: (error) => {
          const errorAxios = error as AxiosError<Error>;

          if (errorAxios?.response?.data && errorAxios?.response?.data.field !== "unknown")
            setResponseError(errorAxios?.response?.data);
        },
      }
    );
  };

  const handleSubmit = () => {
    setHasFormBeenSubmitted(true);

    if (isValid()) {
      if (mode === ManageModalMode.UPDATE && commodityCode) {
        handleUpdateCommodityCode(commodityCode);

        return;
      }

      if (commodityCode && selectedParent) {
        // Make sure we're sending the hscode with leading zeros if necessary
        const lastDigits = formatCommodityCode(commodityCode.hscode);
        const hscode = `${commodityCode.parent}${lastDigits.padStart(4, "0")}`;

        handleCreateCommodityCode({
          ...commodityCode,
          hscode,
          status: commodityCode.status || Status.ACTIVE,
          companyId: currentUser?.companyId,
          description: selectedParent?.description,
          level: CodeTypeNumber[CodeType.COMMODITY_CODE],
          sectionId: selectedParent?.sectionId,
          chapterId: selectedParent?.chapterId,
        });
      }
    }
  };

  const baseFieldProps = (key: keyof CommodityCode) => {
    return {
      name: key,
      disabled: isLoading,
      label: translate(key),
      value: commodityCode?.[key],
      dataCy: key,
      placeholder: translate(key),
      error: fieldHasError(key),
      errorMessage: getErrorByField(key),
      required: true,
    };
  };

  useEffect(() => {
    if (hasFormBeenSubmitted) isValid();
  }, [hasFormBeenSubmitted, commodityCode]);

  const setFormattedCommodityCode = (value: string) => {
    if (Number(value) < 0 || value.length > 4) return;
    if (!REGEX_TEST_NUMERIC.test(value)) return;

    if (commodityCode?.parent) {
      handleUpdateState("hscode", `${commodityCode.parent}${value}`);
      setResponseError(undefined);

      return;
    }

    handleUpdateState("hscode", `000000${value}`);
    setResponseError(undefined);
  };

  const fields: FieldViewWithGridProps[][] = [
    [
      {
        label: `${translate("section")} ${selectedParent?.sectionEntity?.code || ""}`,
        value: selectedParent?.sectionEntity?.description,
        collapse: false,
      },
      {
        label: `${translate("chapter")} ${selectedParent?.chapterEntity?.code || ""}`,
        value: selectedParent?.chapterEntity?.description,
        collapse: false,
      },
      {
        label: `${translate("description")} ${
          selectedParent?.hscode ? formatHSCode(selectedParent?.hscode) : ""
        }`,
        value: selectedParent?.description,
        collapse: false,
      },
    ],
  ];

  const titleAction = useMemo(() => {
    switch (mode) {
      case ManageModalMode.CREATE:
        return translate("create");
      case ManageModalMode.UPDATE:
        return translate("edit");
      case ManageModalMode.CLONE:
        return translate("clone");
      default:
        return "";
    }
  }, [mode]);

  const saveButtonTitle = useMemo(() => {
    switch (mode) {
      case ManageModalMode.CREATE:
        return translate("create");
      case ManageModalMode.UPDATE:
        return translate("save");
      case ManageModalMode.CLONE:
        return translate("clone");
      default:
        return "";
    }
  }, [mode]);

  const booleanStatus = commodityCode?.status === Status.ACTIVE;

  return (
    <Modal
      title={`${titleAction} ${translate("hsCommodityCode")}`}
      open={modalOpened}
      handleClose={closeModal}
      showModalFooter
      showActionButtons
      actionButtonProps={{
        loading: isLoading,
        onCancel: closeModal,
        onSave: handleSubmit,
        saveDisabled: isLoading || !hasUnsavedChanges,
        showSaveButton: true,
        showCancelButton: true,
        customColorCancel: Color.GRAY,
        textSave: saveButtonTitle,
      }}
      labelStatus={translate("status")}
      showStatusSwitch
      statusSwitchProps={{
        checked: booleanStatus,
        handleChange: (_: boolean, status: Status) => handleUpdateState("status", status),
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={2}
        data-cy="send-shipment-email-modal"
      >
        {mode === ManageModalMode.CLONE && (
          <Grid item xs={12}>
            <Subtitle>{translate("adjustTheDataAsDesiredToCreateTheNewCode")}</Subtitle>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <SkeletonComponent isLoading={false}>
            <AutoSuggest
              {...baseFieldProps("parent")}
              disabled={mode === ManageModalMode.UPDATE || mode === ManageModalMode.CLONE}
              label={translate("hsCode")}
              placeholder={translate("typeInTheFirst6Digits")}
              getOptionLabel={(option: CommodityCode) => option?.hscode}
              getOptionSelected={(option: CommodityCode, value: CommodityCode) =>
                option.id === value.id
              }
              renderOption={(props, option) => <div {...props}>`${formatHSCode(option?.hscode)} - ${option?.description}`</div>}
              inputValue={commodityCode?.parent && formatHSCode(commodityCode?.parent)}
              onChange={(value: CommodityCode) => {
                if (!value?.hscode) return;
                handleUpdateState("parent", value.hscode);
                setSelectedParent(value);
              }}
              filterOptions={(options) => options}
              onSearch={(value) =>
                CommodityCodeService.getAll({
                  companyId: currentUser?.companyId,
                  level: CodeTypeNumber[CodeType.HS_CODE],
                  sortBy: JSON.stringify([["section", "ASC"]]),
                  searchByOr: value
                    ? JSON.stringify([{ description: value }, { hscode: value }])
                    : "",
                  include: JSON.stringify([
                    { association: "sectionEntity" },
                    { association: "chapterEntity" },
                  ]),
                })
              }
            />
          </SkeletonComponent>
        </Grid>
        <Grid item xs={12} md={6}>
          <NumberInput
            {...baseFieldProps("hscode")}
            label={translate("commodityCode")}
            type="number"
            placeholder={translate("typeInTheLast2To4Digits")}
            value={commodityCode?.hscode && formatCommodityCode(commodityCode?.hscode)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFormattedCommodityCode(event.target.value);
            }}
            errorResponse={responseError}
          />
        </Grid>
        <Grid item xs={12}>
          <SectionView noPadding md={12} fields={fields} />
        </Grid>
        <Grid item xs={12}>
          <Input
            {...baseFieldProps("statisticalClassification")}
            multiline
            rows={2}
            maxRows={4}
            placeholder={translate("typeInTheCommodityCodeDescription")}
            value={commodityCode?.statisticalClassification}
            onChange={(event) => {
              handleUpdateState("statisticalClassification", event.target.value);
            }}
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <SkeletonComponent isLoading={variables.fetchingRegions}>
            <AutoSuggest
              {...baseFieldProps("regions")}
              multiple
              value={commodityCode?.regions || []}
              label={translate("applicableTo")}
              placeholder={translate("typeInCountryOrRegion")}
              getOptionLabel={(option: RegionCommodityCode) => option?.name}
              getOptionSelected={(option: RegionCommodityCode, value: RegionCommodityCode) =>
                option.id === value.id
              }
              renderOption={(props, option) => <div {...props}>{option.name}</div>}
              onChange={(value: RegionCommodityCode[]) => {
                handleUpdateState("regions", value);
              }}
              options={variables.regions}
            />
          </SkeletonComponent>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default forwardRef(ManageCommodityCodeModal);
